import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { FraudService } from "@gen/tracker/v1/fraud_connect";
import { ClickLabel, type GetFraudOverviewResponse, type ScoringCatalog } from "@gen/tracker/v1/fraud_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { DateRange } from "@gen/tracker/v1/common_pb";
import { clickKeys } from "@/lib/services/click-service";

const client = createClient(FraudService);

export const fraudKeys = {
  all: ["fraud"] as const,
  overview: (from: string, to: string, campaignId?: string) =>
    [...fraudKeys.all, "overview", from, to, campaignId ?? ""] as const,
  proposals: (workspaceId?: string) => [...fraudKeys.all, "proposals", workspaceId ?? ""] as const,
  activeWeights: (workspaceId?: string) => [...fraudKeys.all, "active-weights", workspaceId ?? ""] as const,
};

export interface FraudOverviewParams {
  range: { from: string; to: string };
  campaignId?: string;
}

export function useFraudOverview(params: FraudOverviewParams) {
  return useQuery<GetFraudOverviewResponse>({
    queryKey: fraudKeys.overview(params.range.from, params.range.to, params.campaignId),
    placeholderData: keepPreviousData,
    queryFn: () =>
      client.getFraudOverview({
        range: new DateRange({ from: params.range.from, to: params.range.to }),
        campaignId: params.campaignId ?? "",
      }),
  });
}

/** LabelClick mutation. Invalidates the click trace so the drawer reflects the
 * new label, and fraud proposals (a fresh label may seed the next tuning run). */
export function useLabelClick() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { clickId: string; label: ClickLabel; note?: string }) =>
      client.labelClick({ clickId: input.clickId, label: input.label, note: input.note ?? "" }),
    onSuccess: (_res, input) => {
      void qc.invalidateQueries({ queryKey: clickKeys.trace(input.clickId) });
    },
  });
}

export function useProposedWeights(workspaceId?: string) {
  return useQuery({
    queryKey: fraudKeys.proposals(workspaceId),
    queryFn: async () => {
      const res = await client.listProposedWeights({ workspaceId: workspaceId ?? "" });
      return res.items;
    },
  });
}

export function useActivateWeights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.activateWeights({ id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: fraudKeys.all }),
  });
}

/** Effective active weight set for a scope + shipped defaults, for the manual
 * tuning editor. workspaceId "" = the global scope. */
export function useActiveWeights(workspaceId?: string) {
  return useQuery({
    queryKey: fraudKeys.activeWeights(workspaceId),
    queryFn: () => client.getActiveWeights({ workspaceId: workspaceId ?? "" }),
  });
}

export function useSaveWeights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId?: string; catalog: PlainMessage<ScoringCatalog>; note?: string }) =>
      client.saveWeights({
        workspaceId: input.workspaceId ?? "",
        catalog: input.catalog,
        note: input.note ?? "",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: fraudKeys.all }),
  });
}

export const CLICK_LABEL = ClickLabel;
